import React, { useRef } from 'react'
import { Box } from '@mui/material'
import { PhoneIcon } from '@heroicons/react/24/outline'
import Draggable, { type DraggableData, type DraggableEvent } from 'react-draggable'
import { INTERAX_COLORS } from '../../../constants/colors'

interface FloatingCallButtonProps {
  position: { x: number; y: number }
  ariaLabel: string
  onRestore: () => void
  onPositionChange: (position: { x: number; y: number }) => void
}

export const FloatingCallButton: React.FC<FloatingCallButtonProps> = ({
  position,
  ariaLabel,
  onRestore,
  onPositionChange
}) => {
  const nodeRef = useRef<HTMLDivElement>(null)
  const dragMovedRef = useRef(false)
  const suppressClickRef = useRef(false)

  const handleStart = () => {
    dragMovedRef.current = false
  }

  const handleDrag = (_event: DraggableEvent, data: DraggableData) => {
    if (dragMovedRef.current) return
    if (Math.abs(data.x - position.x) > 1 || Math.abs(data.y - position.y) > 1) {
      dragMovedRef.current = true
    }
  }

  const handleStop = (_event: DraggableEvent, data: DraggableData) => {
    const nextPosition = { x: data.x, y: data.y }
    onPositionChange(nextPosition)

    if (dragMovedRef.current) {
      suppressClickRef.current = true
      window.setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
    }
  }

  const handleClick = () => {
    if (suppressClickRef.current) return
    onRestore()
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      bounds="body"
      position={position}
      onStart={handleStart}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <Box
        ref={nodeRef}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 72,
          height: 72,
          borderRadius: '50%',
          backgroundColor: INTERAX_COLORS.white,
          boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          zIndex: 1300,
          outline: 'none',
          border: `2px solid ${INTERAX_COLORS.sun}`
        }}
      >
        <PhoneIcon style={{ width: '28px', height: '28px', color: INTERAX_COLORS.sun }} />
      </Box>
    </Draggable>
  )
}

export default FloatingCallButton
